// meetLiveRecap.js — THE CALL'S SUMMARY BUILDS DURING THE CALL (Nika
// 2026-10-23: "make call ending page summary during the call also realtime -
// so if I catch events, tasks, some workspace additions, it can show up in
// dashboard - so it automatically catches the data events - does not need to
// wait for the call end. but also shows on the end screen of the call").
//
// While a transcribed call runs, its analysis — the summary and what the
// conversation caught: events, tasks, notes, knowledge, messages
// (AiChatService.meetAnalyze, server) — is asked AGAIN as the transcript
// grows: once at least LINES new lines have landed and GAP_MS have passed
// since the last ask, never two at once. The answer is the room's ONE
// analysis row; the server pushes it to everyone in the call on the room's own
// stream (`meet.analysis.update` → root.analysis, subscribeMeetRealtime.js),
// so the call's live summary (MeetLiveRecap, in the transcript drawer) and
// the end screen (MeetTranscriptPage) read the same row — the end screen has
// it the moment the call ends. Leaving asks once more for the last lines
// (meetLiveRecapForce).
//
// ONE participant asks: the room's creator while they are in the call, else
// the live participant whose id sorts first — a room of five makes one model
// call, not five; no coordination needed (everyone computes the same one).
//
// The bookkeeping (lines since the last ask, when it was, one in flight) lives
// on the root under a double-underscore key — never state, never rendered.
//
// FRANK: every export is self-contained (FA204); siblings through `this.call`.

// The participant who asks for this room's analysis, or null.
export function meetLiveRecapAsker(root, roomId) {
  const r = root && typeof root === 'object' ? root : {}
  if (!roomId) return null
  const room = (Array.isArray(r.rooms) ? r.rooms : []).find((x) => x && x.id === roomId)
  const live = ((r.liveParticipants && r.liveParticipants[roomId]) || []).filter(
    (p) => p && !p.leftAt && p.userId
  )
  const ids = [...new Set(live.map((p) => String(p.userId)))].sort()
  const creator = room && room.createdBy ? String(room.createdBy) : ''
  if (creator && ids.includes(creator)) return creator
  return ids[0] || null
}

// A transcript line landed (subscribeMeetRealtime's transcript handler): count
// it, and ask for the analysis again when it is this participant's turn.
export function meetLiveRecap(u) {
  const root = this.state && this.state.root
  if (!root || !u || !u.roomId) return false
  const LINES = 6
  const GAP_MS = 60000
  const roomId = u.roomId
  if (roomId !== root.activeRoomId) return false
  const room = (Array.isArray(root.rooms) ? root.rooms : []).find((x) => x && x.id === roomId)
  if (room && room.transcribeEnabled === false) return false
  const bag = (root.__meetLiveRecap = root.__meetLiveRecap || {})
  const rec = (bag[roomId] = bag[roomId] || { lines: 0, at: 0, busy: false })
  rec.lines += 1
  // both id forms of this person: a room's created_by and a participant's
  // user_id can carry the legacy one (ControlBar's EndRoomBtn compares both)
  const mine = [root.currentUserSupabaseId, root.currentUserId].filter(Boolean).map(String)
  if (!mine.includes(String(this.call('meetLiveRecapAsker', root, roomId) || ''))) return false
  if (rec.busy || rec.lines < LINES || Date.now() - rec.at < GAP_MS) return false
  return this.call('meetLiveRecapAsk', roomId)
}

// Ask now (the turn above): one in flight per room.
export function meetLiveRecapAsk(roomId) {
  const root = this.state && this.state.root
  if (!root || !roomId) return false
  const bag = (root.__meetLiveRecap = root.__meetLiveRecap || {})
  const rec = (bag[roomId] = bag[roomId] || { lines: 0, at: 0, busy: false })
  if (rec.busy) return false
  rec.busy = true
  rec.lines = 0
  rec.at = Date.now()
  const done = () => {
    rec.busy = false
  }
  try {
    const run = this.call('analyzeTranscript', roomId, true)
    if (run && typeof run.then === 'function') {
      run.then(done, (err) => {
        done()
        // no transcript yet, a slow model: the next lines ask again
        if (!(err && err.pending)) console.warn('[meet] live recap failed:', err && err.message)
      })
    } else done()
  } catch (err) {
    done()
    console.warn('[meet] live recap failed:', err && err.message)
  }
  return true
}

// Leaving the call (ControlBar's Leave, read BEFORE the disconnect, while the
// room's participants still include this one): should the end screen ask the
// analysis fresh? Yes when this participant is the one who asks and lines have
// landed since the last ask — the end screen then has the call's last minutes;
// anyone else reads the live row as it is (no second model call).
export function meetLiveRecapForce(roomId) {
  const root = this.state && this.state.root
  if (!root || !roomId) return false
  const rec = root.__meetLiveRecap && root.__meetLiveRecap[roomId]
  if (!rec || rec.lines <= 0 || rec.busy) return false
  const mine = [root.currentUserSupabaseId, root.currentUserId].filter(Boolean).map(String)
  if (!mine.includes(String(this.call('meetLiveRecapAsker', root, roomId) || ''))) return false
  rec.lines = 0
  rec.at = Date.now()
  return true
}

// The live summary's model: the active room's analysis, when it is this
// room's — its summary and the counts of what it caught, by bucket.
export function meetLiveRecapOf(root) {
  const r = root && typeof root === 'object' ? root : {}
  const a = r.analysis
  const roomId = r.activeRoomId
  if (!a || !roomId || (a.room_id && a.room_id !== roomId) || (a.roomId && a.roomId !== roomId))
    return null
  const sug = (a.suggestions && typeof a.suggestions === 'object' && a.suggestions) || {}
  // the end screen's bucket order and words (meetUtils.js SUGGESTION_BUCKETS)
  const LABELS = {
    calendar_events: 'meet.bucket.calendar',
    tickets: 'meet.bucket.tickets',
    notes: 'meet.bucket.notes',
    knowledge_base: 'meet.bucket.knowledgeBase',
    chat_messages: 'meet.bucket.chat'
  }
  const counts = Object.keys(LABELS)
    .map((b) => ({
      bucket: b,
      label: LABELS[b],
      count: Array.isArray(sug[b]) ? sug[b].length : 0
    }))
    .filter((x) => x.count > 0)
  return {
    summary: typeof a.summary === 'string' ? a.summary : '',
    counts,
    total: counts.reduce((n, x) => n + x.count, 0),
    at: a.generated_at || a.generatedAt || null
  }
}
